import { ReactNode } from "react";
import { Block, CheckBoxOutlineBlank, EditDocument } from "@mui/icons-material";
import { Alert, AlertTitle } from "@mui/material";

import { UserIndicator } from "@/types/user";

import { getErrorMessage } from "@/lib/data";

// general assistance verbiage based on user indicator
export function additionalAssistanceClause(userIndicator: UserIndicator) {
  const studentAssistance = (
    <>
      stop in or contact{" "}
      <a href="https://www.morainepark.edu/experience-mptc/facilities/building-hours/">
        Student Services
      </a>{" "}
      Monday - Friday during operational hours at{" "}
      <a href="tel:+18004724554">1-800-472-4554</a>
    </>
  );

  const nonStudentAssistance = (
    <>
      call the Technology Help Line at{" "}
      <a href="tel:+18772303509">877-230-3509</a> or email{" "}
      <a href="mailto:askit@morainepark.edu">AskIT@morainepark.edu</a>
    </>
  );

  const assistanceVerbiage =
    userIndicator === "ST" ? studentAssistance : nonStudentAssistance;

  return (
    <>
      <br />
      <br />
      If you need help, please {assistanceVerbiage}. Tell them about your
      problem, and someone will help you as soon as they can.
    </>
  );
}

// general error
export function getGeneralErrorAlert(
  message: string,
  error: unknown,
  userIndicator: UserIndicator
) {
  return (
    <Alert icon={<Block />} severity="error">
      <AlertTitle>{message}</AlertTitle>
      {getErrorMessage(error)}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

export function getLookupAlert() {
  return (
    <Alert
      color="info"
      icon={<EditDocument color="primary" />}
      sx={{ width: "100%" }}
    >
      Please fill in all fields and then click &quot;Lookup&quot;
    </Alert>
  );
}

export function getRecaptchaAlert(userIndicator: UserIndicator = "EM") {
  return (
    <Alert icon={<CheckBoxOutlineBlank />} severity="warning">
      <AlertTitle>reCAPTCHA check required</AlertTitle>
      <strong>
        Please complete the reCAPTCHA at the bottom of the screen to continue
      </strong>
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

export function getTokenValidationErrorAlert(
  message: string,
  error: unknown,
  userIndicator: UserIndicator,
  timerElement: ReactNode
) {
  return (
    <Alert icon={<Block />} severity="error">
      <AlertTitle>{message}</AlertTitle>
      {getTokenValidationStatus(timerElement)}
      <br />
      <br />
      {getErrorMessage(error)}
      {additionalAssistanceClause(userIndicator)}
    </Alert>
  );
}

export function getTokenValidationStatus(timerElement: ReactNode) {
  if (!timerElement)
    return (
      <strong>
        The link from your email has expired or already been used.
      </strong>
    );
  else
    return (
      <strong>
        The link from your email is valid for {timerElement} and can only be
        opened once.
      </strong>
    );
}
